import {
  Editor,
  Path,
  Point,
  Range,
  Element as SlateElement,
  Transforms,
} from 'slate'
import type { CustomEditor } from '../../custom-types'

export function withTables<T extends CustomEditor>(editor: T) {
  const {
    deleteBackward,
    deleteForward,
    insertBreak,
    insertSoftBreak,
    insertData,
    insertText,
  } = editor

  editor.deleteBackward = (unit) => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })

      if (cell) {
        const [, cellPath] = cell
        const start = Editor.start(editor, cellPath)

        if (Point.equals(selection.anchor, start)) {
          return
        }
      }
    }

    deleteBackward(unit)
  }

  editor.deleteForward = (unit) => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })

      if (cell) {
        const [, cellPath] = cell
        const end = Editor.end(editor, cellPath)

        if (Point.equals(selection.anchor, end)) {
          return
        }
      }
    }

    deleteForward(unit)
  }

  editor.insertSoftBreak = () => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })

      if (cell) {
        Transforms.insertText(editor, '\n')
        return
      }
    }

    insertSoftBreak()
  }

  editor.insertBreak = () => {
    const { selection } = editor

    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table',
      })

      if (table) {
        return
      }
    }

    insertBreak()
  }

  editor.insertData = (data) => {
    const { selection } = editor

    if (selection) {
      const [cell] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })

      if (cell) {
        insertText(data.getData('text/plain'))
        return
      }
    }

    insertData(data)
  }

  return editor
}

export function isInTable(editor: Editor) {
  const { selection } = editor
  if (!selection) return false

  const [table] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (n.type === 'table' || n.type === 'table-row' || n.type === 'table-cell'),
  })

  return !!table
}

export function insertTable(editor: Editor) {
  Transforms.insertNodes(editor, [
    {
      type: 'table',
      children: [
        {
          type: 'table-row',
          children: [
            {
              type: 'table-cell',
              children: [{ text: '' }],
            },
            {
              type: 'table-cell',
              children: [{ text: '' }],
            },
          ],
        },
        {
          type: 'table-row',
          children: [
            {
              type: 'table-cell',
              children: [{ text: '' }],
            },
            {
              type: 'table-cell',
              children: [{ text: '' }],
            },
          ],
        },
      ],
    },
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
  ])
}

export function addRow(editor: Editor) {
  const { selection } = editor
  if (!selection) return

  const [table] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  })
  if (!table) return

  const [row] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'table-row',
  })
  if (!row) return

  if (!SlateElement.isElement(table[0])) return
  if (!SlateElement.isElement(table[0].children[0])) return

  const cellCountPerRow = table[0].children[0].children.length
  Transforms.insertNodes(
    editor,
    {
      type: 'table-row',
      children: Array.from({ length: cellCountPerRow }, () => ({
        type: 'table-cell',
        children: [{ text: '' }],
      })),
    },
    { at: Path.next(row[1]) },
  )
}

export function removeRow(editor: Editor) {
  const { selection } = editor
  if (!selection) return

  const [table] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  })
  if (!table) return

  const [row] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'table-row',
  })
  if (!row) return

  Transforms.removeNodes(editor, { at: row[1] })
}

export function addColumn(editor: Editor) {
  const { selection } = editor
  if (!selection) return

  const [table] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  })
  if (!table || !SlateElement.isElement(table[0])) return

  const [currentCell] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'table-cell',
  })

  if (!currentCell) return

  const cellPath = currentCell[1]
  const cellIndex = cellPath[cellPath.length - 1]!

  table[0].children.forEach((_, index) => {
    const rowPath = [...table[1], index]
    const [currentRow] = Editor.nodes(editor, {
      at: rowPath,
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === 'table-row',
    })

    if (currentRow && SlateElement.isElement(currentRow[0])) {
      Transforms.insertNodes(
        editor,
        {
          type: 'table-cell',
          children: [{ text: '' }],
        },
        { at: [...rowPath, cellIndex + 1] },
      )
    }
  })
}

export function removeColumn(editor: Editor) {
  const { selection } = editor
  if (!selection) return

  const [table] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
  })
  if (!table || !SlateElement.isElement(table[0])) return

  const [currentCell] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'table-cell',
  })

  if (!currentCell) return

  const cellPath = currentCell[1]
  const cellIndex = cellPath[cellPath.length - 1]

  table[0].children.forEach((_, index) => {
    const rowPath = [...table[1], index]
    Transforms.removeNodes(editor, { at: [...rowPath, cellIndex!] })
  })
}
